import { Fragment, useEffect, useId, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    minWidth: 0,
    maxWidth: '100%',
  },
  section: { padding: theme.spacing(2) },
  heading: { fontWeight: 600 },
  controls: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 2fr) repeat(2, minmax(180px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  confirmation: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  disclosure: {
    marginTop: theme.spacing(1),
    '& summary': {
      cursor: 'pointer',
      color: theme.palette.text.secondary,
      fontSize: theme.typography.body2.fontSize,
    },
    '& summary:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '& p': { marginTop: theme.spacing(1) },
  },
  tableContainer: {
    maxWidth: '100%',
    overflowX: 'auto',
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  table: {
    minWidth: 1120,
    '& th, & td': { verticalAlign: 'top' },
    '& .MuiTableCell-root:not(.MuiTableCell-paddingCheckbox)': {
      padding: theme.spacing(1, 1.5),
    },
  },
  tableHead: {
    backgroundColor: theme.palette.action.hover,
    '& th': { fontWeight: 600, whiteSpace: 'nowrap' },
  },
  selection: { width: 48 },
  server: { minWidth: 180 },
  serverName: { maxWidth: 240, overflowWrap: 'break-word', fontWeight: 600 },
  location: { minWidth: 140 },
  network: { minWidth: 200 },
  compute: { minWidth: 120 },
  os: { minWidth: 220 },
  ssm: { minWidth: 90 },
  line: { display: 'block' },
  identifier: {
    display: 'block',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
  },
  rowDisclosure: { marginTop: theme.spacing(1) },
  evidenceStatus: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.caption.fontSize,
  },
  detailsCell: { backgroundColor: theme.palette.action.hover },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    alignItems: 'start',
  },
  detailCard: { padding: theme.spacing(2), minWidth: 0, overflowX: 'auto' },
  facts: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    gap: theme.spacing(1, 2),
    margin: theme.spacing(1, 0, 0),
    '& dt': { color: theme.palette.text.secondary },
    '& dd': { margin: 0 },
  },
  provenance: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(2),
    '& li + li': { marginTop: theme.spacing(2) },
  },
  scope: { maxWidth: '100%', overflowX: 'auto' },
}));

export type InstanceCandidate = {
  key: string;
  partition: 'aws' | 'aws-us-gov' | 'aws-cn';
  accountId: string;
  connectionId: string;
  region: string;
  networkId: string;
  instanceId: string;
  name?: string;
  availabilityZone?: string;
  state?: string;
  instanceType?: string;
  ssmStatus?: string;
  subnetId?: string;
  architecture?: string;
  launchImageId?: string;
  sourceOs?: {
    family: 'LINUX' | 'WINDOWS' | 'MACOS' | 'OTHER' | 'UNKNOWN';
    distribution?: string;
    version?: string;
    status: 'VERIFIED' | 'LIMITED' | 'STALE' | 'CONFLICT' | 'UNAVAILABLE';
    capturedAt?: string;
    retrievedAt: string;
    reason: string;
    evidence: {
      source: string;
      capturedAt?: string;
      retrievedAt: string;
      family: string;
      distribution?: string;
      version?: string;
      freshness: string;
    }[];
  };
};

export function osMatches(candidate: InstanceCandidate, view: string) {
  if (!view) return true;
  const os = candidate.sourceOs;
  const reportedFamily = os && ['VERIFIED', 'LIMITED'].includes(os.status);
  if (view === 'UNVERIFIED')
    return os?.status !== 'VERIFIED' || os.family === 'UNKNOWN';
  if (view === 'UNSUPPORTED')
    return reportedFamily && ['MACOS', 'OTHER'].includes(os.family);
  return reportedFamily && os.family === view;
}

function isVerifiedOs(os: InstanceCandidate['sourceOs']) {
  return !!os && os.status === 'VERIFIED' && os.family !== 'UNKNOWN';
}

function osNextAction(os: InstanceCandidate['sourceOs']) {
  if (os?.status === 'CONFLICT') {
    return 'Reconcile conflicting OS evidence with separately authorized current guest evidence. Guest inspection requires working SSM.';
  }
  if (os?.status === 'STALE') {
    return 'Refresh stale OS evidence through separately authorized guest inspection with working SSM.';
  }
  if (os?.status === 'LIMITED') {
    return 'Reported OS details are limited observations, not current OS/kernel proof. Verify through fresh inventory or separately authorized guest inspection with working SSM.';
  }
  if (!isVerifiedOs(os) || !os?.distribution || !os.version) {
    return 'Obtain separately authorized guest evidence to verify unknown OS details. Guest inspection requires working SSM.';
  }
  return 'Review evidence before acceptance. Discovery does not authorize guest inspection.';
}

function evidenceTime(value?: string) {
  return value ? <time dateTime={value}>{value}</time> : 'unknown';
}

function DetailFacts({ facts }: { facts: [string, ReactNode][] }) {
  const classes = useStyles();
  return (
    <Box component="dl" className={classes.facts}>
      {facts.map(([label, value]) => (
        <Fragment key={label}>
          <Typography component="dt" variant="body2">
            {label}
          </Typography>
          <Typography component="dd" variant="body2">
            {value}
          </Typography>
        </Fragment>
      ))}
    </Box>
  );
}

/** Candidate enumeration and selection are read-only; no collection callbacks. */
export function InstanceSelectionTable({
  candidates,
  selected,
  onChange,
  complete,
  disabled,
  osView = '',
  selectedCandidates = candidates,
}: {
  candidates: InstanceCandidate[];
  selected: string[];
  onChange: (keys: string[]) => void;
  complete: boolean;
  disabled?: boolean;
  osView?: string;
  selectedCandidates?: InstanceCandidate[];
}) {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const fieldId = useId();
  const [state, setState] = useState('');
  const [page, setPage] = useState(0);
  const [bulk, setBulk] = useState(false);
  const [distribution, setDistribution] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);
  useEffect(() => {
    setBulk(false);
  }, [osView, candidates]);
  useEffect(() => {
    setPage(0);
  }, [osView]);
  const matches = candidates.filter(
    candidate =>
      osMatches(candidate, osView) &&
      (!distribution || candidate.sourceOs?.distribution === distribution) &&
      (!state || candidate.state === state) &&
      [
        candidate.name,
        candidate.instanceId,
        candidate.accountId,
        candidate.region,
        candidate.networkId,
        candidate.subnetId,
        candidate.availabilityZone,
        candidate.instanceType,
        candidate.ssmStatus,
        candidate.architecture,
        candidate.sourceOs?.distribution,
        candidate.sourceOs?.version,
      ]
        .join(' ')
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(matches.length / 10) - 1),
  );
  const rows = matches.slice(currentPage * 10, currentPage * 10 + 10);
  const hidden = selected.filter(
    key => !matches.some(candidate => candidate.key === key),
  ).length;
  const add = (items: InstanceCandidate[]) =>
    onChange([...new Set([...selected, ...items.map(item => item.key)])]);
  return (
    <Card variant="outlined" className={classes.root}>
      <Box className={classes.section}>
        <Typography
          variant="h6"
          component="h3"
          id={`${fieldId}-heading`}
          className={classes.heading}
        >
          Select individual instances
        </Typography>
        <Typography
          variant="body2"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {candidates.length} candidates · {selected.length} selected · {hidden}{' '}
          selected hidden by filters
        </Typography>
        <Typography variant="body2">
          {complete
            ? 'Inventory returned for all selected VPCs at discovery time.'
            : 'Partial inventory: one or more VPCs could not be read.'}
        </Typography>
        <details className={classes.disclosure}>
          <summary>Inventory and selection guidance</summary>
          <Typography variant="body2">
            Inventory may change before acceptance. Partial results are not the
            complete inventory. No newly discovered instances are selected
            automatically.
          </Typography>
          <Typography variant="body2">
            SSM unavailable or offline does not exclude an instance. Guest
            inspection requires separate authorization and working SSM.
            Unavailable values are not inferred.
          </Typography>
          <Typography variant="body2">
            Managed Kubernetes worker nodes are excluded from this server
            picker: they are replaceable platform infrastructure, not standalone
            migration candidates.
          </Typography>
        </details>
        <Box className={classes.controls}>
          <TextField
            id={`${fieldId}-search`}
            label="Search instances"
            variant="outlined"
            size="small"
            fullWidth
            value={search}
            disabled={disabled}
            onChange={event => {
              setSearch(event.target.value);
              setPage(0);
              setBulk(false);
            }}
          />
          <TextField
            id={`${fieldId}-distribution`}
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label="Distribution or edition"
            variant="outlined"
            size="small"
            fullWidth
            value={distribution}
            disabled={disabled}
            onChange={event => {
              setDistribution(event.target.value);
              setPage(0);
              setBulk(false);
            }}
          >
            <option value="">All observed distributions</option>
            {[
              ...new Set(
                candidates
                  .filter(item => osMatches(item, osView))
                  .map(item => item.sourceOs?.distribution)
                  .filter(Boolean),
              ),
            ].map(value => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
            {distribution &&
              !candidates.some(
                item =>
                  osMatches(item, osView) &&
                  item.sourceOs?.distribution === distribution,
              ) && (
                <option value={distribution}>
                  {distribution} (not in current view)
                </option>
              )}
          </TextField>
          <TextField
            id={`${fieldId}-state`}
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label="Instance state"
            variant="outlined"
            size="small"
            fullWidth
            value={state}
            disabled={disabled}
            onChange={event => {
              setState(event.target.value);
              setPage(0);
              setBulk(false);
            }}
          >
            <option value="">All states</option>
            {[
              ...new Set(candidates.map(item => item.state).filter(Boolean)),
            ].map(value => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </TextField>
        </Box>
        <Box className={classes.actions}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            disabled={disabled || !rows.length}
            onClick={() => add(rows)}
          >
            Select this page ({rows.length})
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={disabled || !matches.length || matches.length > 200}
            onClick={() => setBulk(true)}
          >
            Select all matching instances ({matches.length})
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={disabled || !selected.length}
            onClick={() => onChange([])}
          >
            Clear instance selection
          </Button>
        </Box>
        {bulk && (
          <Card
            variant="outlined"
            className={classes.confirmation}
            role="alert"
          >
            <Typography variant="subtitle2" component="h4">
              Review bulk selection
            </Typography>
            <Typography variant="body2">
              This will add all {matches.length} currently visible matching
              candidates across{' '}
              {new Set(matches.map(item => item.accountId)).size} accounts and{' '}
              {
                new Set(
                  matches.map(
                    item =>
                      `${item.accountId}/${item.region}/${item.networkId}`,
                  ),
                ).size
              }{' '}
              VPC scopes. Existing selections hidden by filters remain selected.
              No newly discovered instances will be added automatically.
            </Typography>
            <Box className={classes.actions}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                disabled={disabled}
                onClick={() => {
                  add(matches);
                  setBulk(false);
                }}
              >
                Confirm bulk selection of {matches.length} instances
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setBulk(false)}
              >
                Cancel bulk selection
              </Button>
            </Box>
          </Card>
        )}
        <Box mt={2}>
          <Typography
            variant="caption"
            color="textSecondary"
            id={`${fieldId}-scroll-help`}
          >
            Scroll horizontally on smaller screens. Expand server details for OS
            provenance and next steps.
          </Typography>
        </Box>
      </Box>
      <TableContainer
        className={classes.tableContainer}
        tabIndex={0}
        role="region"
        aria-label="Scrollable candidate instances"
        aria-describedby={`${fieldId}-scroll-help`}
      >
        <Table
          size="small"
          className={classes.table}
          aria-label="Candidate EC2 instances"
        >
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell
                scope="col"
                padding="checkbox"
                className={classes.selection}
              >
                Select
              </TableCell>
              <TableCell scope="col" className={classes.server}>
                Server
              </TableCell>
              <TableCell scope="col" className={classes.location}>
                Location
              </TableCell>
              <TableCell scope="col" className={classes.network}>
                Network
              </TableCell>
              <TableCell scope="col" className={classes.compute}>
                Compute
              </TableCell>
              <TableCell scope="col" className={classes.os}>
                OS
              </TableCell>
              <TableCell scope="col" className={classes.ssm}>
                SSM
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(item => {
              const os = item.sourceOs;
              const currentOs = isVerifiedOs(os);
              const detailsId = `${fieldId}-details-${encodeURIComponent(
                item.key,
              )}`;
              const isExpanded = expanded.includes(item.key);
              return (
                <Fragment key={item.key}>
                  <TableRow selected={selected.includes(item.key)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        inputProps={{
                          'aria-label': `Select ${item.instanceId} in ${item.accountId}/${item.region}`,
                        }}
                        disabled={disabled}
                        checked={selected.includes(item.key)}
                        onChange={(_, checked) =>
                          onChange(
                            checked
                              ? [...selected, item.key]
                              : selected.filter(key => key !== item.key),
                          )
                        }
                      />
                    </TableCell>
                    <TableCell component="th" scope="row" role="rowheader">
                      <Typography
                        variant="body2"
                        className={classes.serverName}
                      >
                        {item.name || 'Unnamed server'}
                      </Typography>
                      <Typography
                        variant="caption"
                        className={classes.identifier}
                      >
                        {item.instanceId}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        className={classes.rowDisclosure}
                        aria-label={`${
                          isExpanded ? 'Hide details' : 'Details'
                        } for ${item.instanceId} in ${item.accountId}/${
                          item.region
                        }`}
                        aria-expanded={isExpanded}
                        aria-controls={isExpanded ? detailsId : undefined}
                        onClick={() =>
                          setExpanded(keys =>
                            keys.includes(item.key)
                              ? keys.filter(key => key !== item.key)
                              : [...keys, item.key],
                          )
                        }
                      >
                        {isExpanded ? 'Hide details' : 'Details'}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        className={classes.identifier}
                      >
                        Account: {item.accountId}
                      </Typography>
                      <Typography variant="body2" className={classes.line}>
                        {item.region}
                      </Typography>
                      <Typography variant="caption" className={classes.line}>
                        AZ: {item.availabilityZone || 'Unavailable'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        className={classes.identifier}
                      >
                        VPC: {item.networkId}
                      </Typography>
                      <Typography
                        variant="caption"
                        className={classes.identifier}
                      >
                        Subnet: {item.subnetId || 'Unavailable'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" className={classes.line}>
                        {item.state || 'State unavailable'}
                      </Typography>
                      <Typography variant="caption" className={classes.line}>
                        Type: {item.instanceType || 'Unavailable'}
                      </Typography>
                      <Typography variant="caption" className={classes.line}>
                        Arch: {item.architecture || 'Unavailable'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" className={classes.line}>
                        {os?.distribution
                          ? `${currentOs ? '' : 'Reported: '}${os.distribution}`
                          : 'Distribution unknown'}
                      </Typography>
                      <Typography variant="caption" className={classes.line}>
                        {os?.version
                          ? `${currentOs ? 'Version' : 'Reported version'}: ${
                              os.version
                            }`
                          : 'Version unknown—guest evidence required'}
                      </Typography>
                      <Typography variant="caption" className={classes.line}>
                        {os && os.family !== 'UNKNOWN'
                          ? `${currentOs ? 'Family' : 'Reported family'}: ${
                              os.family
                            }`
                          : 'Family unknown'}
                      </Typography>
                      {!currentOs && (
                        <Typography variant="caption" className={classes.line}>
                          OS not verified
                        </Typography>
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        className={classes.evidenceStatus}
                        label={
                          os
                            ? `Evidence: ${os.status}`
                            : 'OS evidence unavailable'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.ssmStatus || 'Unavailable'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={7} className={classes.detailsCell}>
                        <Box
                          id={detailsId}
                          role="region"
                          aria-labelledby={`${detailsId}-heading`}
                        >
                          <Typography
                            variant="subtitle2"
                            component="h4"
                            id={`${detailsId}-heading`}
                          >
                            Instance details — {item.instanceId}
                          </Typography>
                          <Box className={classes.detailGrid}>
                            <Card
                              variant="outlined"
                              className={classes.detailCard}
                            >
                              <Typography variant="subtitle2" component="h5">
                                OS evidence and next action
                              </Typography>
                              <DetailFacts
                                facts={[
                                  [
                                    'Evidence status',
                                    os?.status || 'UNAVAILABLE',
                                  ],
                                  [
                                    'Reason',
                                    os?.reason || 'OS evidence unavailable',
                                  ],
                                  ['Captured', evidenceTime(os?.capturedAt)],
                                  ['Retrieved', evidenceTime(os?.retrievedAt)],
                                  ['Next action', osNextAction(os)],
                                ]}
                              />
                            </Card>
                            <Card
                              variant="outlined"
                              className={classes.detailCard}
                            >
                              <Typography variant="subtitle2" component="h5">
                                Source identity
                              </Typography>
                              <DetailFacts
                                facts={[
                                  ['Partition', item.partition],
                                  [
                                    'Connection',
                                    <span className={classes.identifier}>
                                      {item.connectionId}
                                    </span>,
                                  ],
                                  [
                                    'Selection key',
                                    <span className={classes.identifier}>
                                      {item.key}
                                    </span>,
                                  ],
                                  [
                                    'Launch AMI',
                                    <>
                                      <span className={classes.identifier}>
                                        {item.launchImageId || 'Unavailable'}
                                      </span>
                                      Not current OS proof.
                                    </>,
                                  ],
                                ]}
                              />
                            </Card>
                          </Box>
                          <Card
                            variant="outlined"
                            className={`${classes.detailCard} ${classes.rowDisclosure}`}
                          >
                            <Typography variant="subtitle2" component="h5">
                              OS evidence provenance
                            </Typography>
                            {os?.evidence.length ? (
                              <ol className={classes.provenance}>
                                {os.evidence.map((evidence, evidenceIndex) => (
                                  <li key={evidenceIndex}>
                                    <Typography variant="body2">
                                      {evidence.source}
                                    </Typography>
                                    <DetailFacts
                                      facts={[
                                        [
                                          'Reported OS',
                                          `${evidence.family || 'unknown'} / ${
                                            evidence.distribution || 'unknown'
                                          } / ${evidence.version || 'unknown'}`,
                                        ],
                                        [
                                          'Freshness',
                                          evidence.freshness || 'unknown',
                                        ],
                                        [
                                          'Captured',
                                          evidenceTime(evidence.capturedAt),
                                        ],
                                        [
                                          'Retrieved',
                                          evidenceTime(evidence.retrievedAt),
                                        ],
                                      ]}
                                    />
                                  </li>
                                ))}
                              </ol>
                            ) : (
                              <Typography variant="body2">
                                No OS evidence provenance available.
                              </Typography>
                            )}
                          </Card>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={7}>
                  No instances match the current filters. Existing selections
                  are unchanged.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={matches.length}
        page={currentPage}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        onPageChange={(_, next) => setPage(next)}
      />
      <Box className={classes.section}>
        <details className={`${classes.disclosure} ${classes.scope}`}>
          <summary>Exact selected scope ({selected.length})</summary>
          {selectedCandidates
            .filter(item => selected.includes(item.key))
            .map(item => (
              <Typography
                key={item.key}
                variant="body2"
                className={classes.identifier}
              >
                {item.partition}/{item.accountId}/{item.region}/{item.networkId}
                /{item.instanceId}
              </Typography>
            ))}
          {selected
            .filter(key => !selectedCandidates.some(item => item.key === key))
            .map(key => (
              <Typography
                key={key}
                variant="body2"
                className={classes.identifier}
              >
                {key} — requires scope reconciliation
              </Typography>
            ))}
        </details>
        {selected.length === 0 && (
          <Typography color="error">
            Select at least one instance. An empty selection never means all.
          </Typography>
        )}
        {selected.length > 200 && (
          <Typography color="error">
            At most 200 instances may be accepted per scope.
          </Typography>
        )}
      </Box>
    </Card>
  );
}
